import { useEffect } from 'react';
import { DataPacket_Kind, Participant, Room, RoomEvent } from 'livekit-client';
import { t } from 'i18next';
import { ELECTRON_MEET_WINDOW_EVENTS } from '@web-chat-core/toolkit/multi-window/constants';
import { showSnackbar } from '@web-chat-core/ui-kit';
import { getParticipantDisplayName } from 'features/ParticipantItem';
import { useUserSettingsContext } from 'contexts';
import { selectEmbeddedWindowData } from 'state/selectors';
import { useAppEnvironmentProvider } from 'contexts/AppEnvironmentContext';
import { type CommandHandlersContext, handleModerationCommand, isBroadcastAction } from './commands/commandHandlers';
import { MODERATION_DATA_TOPIC, type ModerationCommand } from './types';

/**
 * создает обработчик DataReceived для модерации и возвращает функцию отписки.
 */
export const addModerationDataReceivedListener = (room: Room, context: CommandHandlersContext): (() => void) => {
    const handleDataReceived = (
        payload: Uint8Array,
        participant?: Participant,
        _kind?: DataPacket_Kind,
        topic?: string,
    ) => {
        if (topic !== MODERATION_DATA_TOPIC) return;

        try {
            const json = new TextDecoder().decode(payload);
            const cmd = JSON.parse(json) as ModerationCommand;

            const isBroadcast = isBroadcastAction(cmd.action);
            if (!participant && !isBroadcast) return;

            const hasTarget =
                (cmd.targetIdentity != null && cmd.targetIdentity !== '') ||
                (cmd.targetSid != null && cmd.targetSid !== '');
            const hasModerator =
                (cmd.moderatorIdentity != null && cmd.moderatorIdentity !== '') ||
                (cmd.moderatorSid != null && cmd.moderatorSid !== '');
            if (!cmd?.action || !hasModerator) return;
            if (!hasTarget && !isBroadcastAction(cmd.action)) return;

            const isTarget =
                (cmd.targetIdentity != null && room.localParticipant.identity === cmd.targetIdentity) ||
                (cmd.targetSid != null && room.localParticipant.sid === cmd.targetSid);
            console.log('[Moderation] received cmd', {
                action: cmd.action,
                targetIdentity: cmd.targetIdentity,
                targetSid: cmd.targetSid,
                isTarget,
            });
            handleModerationCommand(room, cmd, context);
        } catch (error) {
            console.error('[Moderation] ошибка парсинга данных команды модератора', {
                error,
                payloadLength: payload.length,
                participantIdentity: participant?.identity,
                topic,
            });
        }
    };

    room.on(RoomEvent.DataReceived, handleDataReceived);

    return () => {
        room.off(RoomEvent.DataReceived, handleDataReceived);
    };
};

/**
 * Хук для подписки на команды модерации (DataReceived).
 * Подписка у всех участников (без проверки флага, VCS_MODERATORS_ENABLED), чтобы целевой участник получал команды
 */
export const useModerationEvents = (room: Room | undefined) => {
    const { setAudioEnabled, setVideoEnabled } = useUserSettingsContext();
    const { multiWindowMessagingService } = useAppEnvironmentProvider();

    useEffect(() => {
        if (!room) return;

        const { descriptor } = selectEmbeddedWindowData() || {};
        const unnamed = t('web_call_unnamed_user');
        const context: CommandHandlersContext = {
            showSnackbar,
            getDisplayData: (participant) => ({ name: getParticipantDisplayName(participant, unnamed) }),
            setAudioEnabled,
            setVideoEnabled,
            t,
            onLocalUserKicked: descriptor
                ? () => {
                      multiWindowMessagingService?.emit(ELECTRON_MEET_WINDOW_EVENTS.USER_KICKED, null, descriptor);
                  }
                : undefined,
        };

        return addModerationDataReceivedListener(room, context);
    }, [room, setAudioEnabled, setVideoEnabled, multiWindowMessagingService]);
};
